import { useState } from "react";
import { Alert, Button, Col, FormControl, FormLabel, FormSelect, Row } from "react-bootstrap";
import { IAISectionCreationBrief } from "./types";
import styles from "./NewPageWizard.module.scss";

interface INewSectionWizard {
  aiAvailable: boolean;
  onCancel: () => void;
  onManualSectionCreated: () => void | Promise<void>;
  onAISectionCreated: (brief: IAISectionCreationBrief) => void | Promise<void>;
}

const defaultBrief: IAISectionCreationBrief = {
  sectionKind: "hero",
  primaryGoal: "",
  targetAudience: "",
  tone: "confident and practical",
  contentInstructions: ""
};

const steps = [
  "Choose path",
  "AI brief"
];

const NewSectionWizard = ({ aiAvailable, onCancel, onManualSectionCreated, onAISectionCreated }: INewSectionWizard): JSX.Element => {
  const [creationMode, setCreationMode] = useState<"manual" | "ai" | null>(null);
  const [stepIndex, setStepIndex] = useState<number>(0);
  const [brief, setBrief] = useState<IAISectionCreationBrief>(defaultBrief);
  const [showValidation, setShowValidation] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const updateBrief = (name: keyof IAISectionCreationBrief, value: string): void => {
    setBrief((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const validateBrief = (): boolean => {
    const valid = brief.sectionKind.trim().length > 0 && brief.contentInstructions.trim().length > 0;
    setShowValidation(!valid);
    return valid;
  };

  const handleContinue = async (): Promise<void> => {
    if (stepIndex === 0) {
      if (creationMode === "manual") {
        setIsSaving(true);
        try {
          await onManualSectionCreated();
        } finally {
          setIsSaving(false);
        }
      }

      if (creationMode === "ai") {
        setStepIndex(1);
      }
      return;
    }

    if (!validateBrief()) {
      return;
    }

    setIsSaving(true);
    try {
      await onAISectionCreated(brief);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = (): void => {
    if (stepIndex === 0) {
      onCancel();
      return;
    }

    setStepIndex(0);
  };

  return (
    <div className={styles.wizard}>
      <div className={styles.stepper}>
        {steps.slice(0, stepIndex === 0 ? 1 : 2).map((step, index) => (
          <div key={step} className={`${styles.step} ${stepIndex === index ? styles.stepActive : ""}`.trim()}>
            <span className={styles.stepNumber}>{index + 1}</span>
            <span>{step}</span>
          </div>
        ))}
      </div>

      {stepIndex === 0 && (
        <div className={styles.panel}>
          <p className={styles.intro}>
            Add the next section manually or let AI draft a single polished section to help move the page forward.
          </p>

          <div className={styles.choices}>
            <button
              type="button"
              className={`${styles.choiceCard} ${creationMode === "manual" ? styles.choiceSelected : ""}`.trim()}
              onClick={() => setCreationMode("manual")}>
              <h3 className={styles.choiceTitle}>Manual Section</h3>
              <p className={styles.choiceText}>
                Open the normal section editor and choose the template, layout, and settings yourself.
              </p>
            </button>

            {aiAvailable && (
              <button
                type="button"
                className={`${styles.choiceCard} ${creationMode === "ai" ? styles.choiceSelected : ""}`.trim()}
                onClick={() => setCreationMode("ai")}>
                <h3 className={styles.choiceTitle}>AI Generated Section</h3>
                <p className={styles.choiceText}>
                  Generate one section with content and presentation guidance so the page feels less blank and more intentional.
                </p>
              </button>
            )}
          </div>
        </div>
      )}

      {stepIndex === 1 && (
        <div className={styles.panel}>
          {showValidation && (
            <Alert variant="warning" className="mb-0">
              Add at least the section type and the main brief before generating the AI section.
            </Alert>
          )}

          <Row className="g-3">
            <Col md={6}>
              <FormLabel>Section type</FormLabel>
              <FormSelect value={brief.sectionKind} onChange={(event) => updateBrief("sectionKind", event.target.value)}>
                <option value="hero">Hero</option>
                <option value="intro">Intro / overview</option>
                <option value="feature-grid">Feature grid</option>
                <option value="cta">Call to action</option>
                <option value="faq">FAQ</option>
                <option value="testimonial">Testimonial / proof</option>
                <option value="custom">Custom section</option>
              </FormSelect>
            </Col>
            <Col md={6}>
              <FormLabel>Primary goal</FormLabel>
              <FormControl
                type="text"
                value={brief.primaryGoal}
                placeholder="Book a demo, explain a feature, add proof..."
                onChange={(event) => updateBrief("primaryGoal", event.target.value)}
              />
            </Col>
            <Col md={6}>
              <FormLabel>Target audience</FormLabel>
              <FormControl
                type="text"
                value={brief.targetAudience}
                placeholder="Editors, marketers, agencies..."
                onChange={(event) => updateBrief("targetAudience", event.target.value)}
              />
            </Col>
            <Col md={6}>
              <FormLabel>Tone of voice</FormLabel>
              <FormControl
                type="text"
                value={brief.tone}
                placeholder="Confident, practical, premium..."
                onChange={(event) => updateBrief("tone", event.target.value)}
              />
            </Col>
            <Col xs={12}>
              <FormLabel>Main section brief</FormLabel>
              <FormControl
                as="textarea"
                rows={6}
                value={brief.contentInstructions}
                placeholder="Describe what this section should achieve, what it should say, how it should feel, and any styling or emphasis you want."
                onChange={(event) => updateBrief("contentInstructions", event.target.value)}
              />
              <div className="form-text">
                This is the primary brief for the section. Use it for the strongest content and design direction.
              </div>
            </Col>
          </Row>
        </div>
      )}

      <div className={styles.footer}>
        <p className={styles.hint}>
          AI sections use the same site context and styling rules as AI version generation, but focus on one section only.
        </p>
        <div className={styles.footerActions}>
          <Button variant="outline-secondary" onClick={handleBack} disabled={isSaving}>
            {stepIndex === 0 ? "Close" : "Back"}
          </Button>
          <Button variant="success" onClick={handleContinue} disabled={!creationMode || isSaving}>
            {stepIndex === 0
              ? creationMode === "manual" ? "Continue" : "Next"
              : isSaving ? "Generating..." : "Generate Section"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NewSectionWizard;
